import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Alert,
  Tooltip as MuiTooltip,
  IconButton
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line
} from 'recharts';
import {
  Timeline,
  Info
} from '@mui/icons-material';
import { authGet, get } from '../../services/apiService';

/**
 * MonthlyAutomationStatusChart component for displaying monthly automation status trends
 * @param {boolean} isPublic - Whether to use public API endpoint
 * @returns {JSX.Element} MonthlyAutomationStatusChart component
 */
const MonthlyAutomationStatusChart = ({ isPublic = false }) => {
  const [data, setData] = useState([]);
  const [automationStatuses, setAutomationStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Simplified automation status mapping
  const mapAutomationStatus = (status) => {
    if (!status) return 'Pending Analysis';
    
    const statusLower = status.toLowerCase();
    
    // Automated group (excluding Automated in Integration Framework - hidden from UI)
    if (statusLower.includes('automated in integration framework')) {
      return 'Pending Analysis'; // Hide from chart
    }
    if (statusLower.includes('automated') ||
        statusLower.includes('automated in new framework') ||
        statusLower.includes('automated in unit framework') ||
        statusLower.includes('automated(old framework)') ||
        statusLower.includes('automated-api') ||
        statusLower.includes('automated-ui')) {
      return 'Automated';
    }
    
    // Manual Test group
    if (statusLower.includes('manual test') || 
        statusLower.includes('manual')) {
      return 'Manual Test';
    }
    
    // Pending Analysis group
    if (statusLower.includes('not automated') || 
        statusLower.includes('tbd')) {
      return 'Pending Analysis';
    }
    
    // To be Automated group
    if (statusLower.includes('ready for epam automation') || 
        statusLower.includes('requires automation')) {
      return 'To be Automated';
    }
    
    // Default fallback
    return 'Pending Analysis';
  };

  // Color palette for simplified automation statuses
  const COLORS = {
    'Automated': '#4caf50',           // Green - Fully automated
    'Manual Test': '#ff9800',         // Orange - Manual tests
    'Pending Analysis': '#9e9e9e',    // Gray - Pending analysis
    'To be Automated': '#2196f3'      // Blue - Ready for automation
  };

  // Fetch monthly automation status data on component mount
  useEffect(() => {
    const fetchMonthlyAutomationData = async () => {
      try {
        setLoading(true);
        
        // Call the API endpoint to get monthly automation status data
        const endpoint = isPublic 
          ? '/api/test/public/monthly-automation-status'
          : '/api/test/monthly-automation-status';
        const response = isPublic 
          ? await get(endpoint)
          : await authGet(endpoint);
        
        if (response.success) {
          // Transform the data to use simplified status groups
          const transformedData = response.data.map(monthData => {
            const simplifiedData = {
              month: monthData.month,
              totalCount: monthData.totalCount
            };
            
            // Group the detailed statuses into simplified categories
            const statusGroups = {
              'Automated': 0,
              'Manual Test': 0,
              'Pending Analysis': 0,
              'To be Automated': 0
            };
            
            // Map each detailed status to its simplified group
            response.automationStatuses.forEach(status => {
              const simplifiedStatus = mapAutomationStatus(status);
              statusGroups[simplifiedStatus] += monthData[status] || 0;
            });
            
            // Add the grouped counts to the data
            Object.keys(statusGroups).forEach(group => {
              simplifiedData[group] = statusGroups[group];
            });
            
            return simplifiedData;
          });
          
          setData(transformedData);
          setAutomationStatuses(['Automated', 'Manual Test', 'Pending Analysis', 'To be Automated']);
          setError(null);
          console.log('Fetched and transformed monthly automation status data:', transformedData);
        } else {
          throw new Error(response.message || 'Failed to load monthly automation status data');
        }
      } catch (err) {
        console.error('Error fetching monthly automation status data:', err);
        setError('Failed to load monthly automation status data. Please try again later.');
        setData([]);
        setAutomationStatuses([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMonthlyAutomationData();
  }, []);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const totalCount = payload.reduce((sum, item) => sum + item.value, 0);
      return (
        <Box sx={{ 
          bgcolor: 'background.paper', 
          border: '1px solid #ccc', 
          borderRadius: 1, 
          p: 2,
          boxShadow: 2,
          minWidth: 200
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {`Month: ${label}`}
          </Typography>
          <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
            Total Tests: {totalCount}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} display="flex" alignItems="center" mb={0.5}>
              <Box 
                sx={{ 
                  width: 12, 
                  height: 12, 
                  bgcolor: entry.color, 
                  mr: 1, 
                  borderRadius: '2px' 
                }} 
              />
              <Typography variant="body2">
                {entry.name}: {entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No monthly automation status data available.
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Timeline sx={{ mr: 1, color: '#2196f3' }} />
            <Typography variant="h6" component="h3">
              Monthly Automation Status Trends (Last 18 Months)
            </Typography>
            <MuiTooltip title="Shows the distribution of test cases by automation status over the last 18 months, grouped by created date">
              <IconButton size="small">
                <Info fontSize="small" />
              </IconButton>
            </MuiTooltip>
          </Box>
        }
      />
      <CardContent>
        <Box height={400}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="month" 
                fontSize={12}
                stroke="#666"
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                label={{ value: 'Number of Tests', angle: -90, position: 'insideLeft' }}
                fontSize={12}
                stroke="#666"
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              
              {/* Stacked bars for each automation status */}
              {automationStatuses.map((status, index) => (
                <Bar 
                  key={status}
                  dataKey={status} 
                  stackId="automation"
                  name={status}
                  fill={COLORS[status] || `hsl(${index * 40}, 70%, 50%)`}
                  radius={index === automationStatuses.length - 1 ? [4, 4, 0, 0] : 0}
                />
              ))}
              
              {/* Line for total trend */}
              <Line
                type="monotone"
                dataKey="totalCount"
                stroke="#ff5722"
                strokeWidth={2}
                name="Total Tests"
                dot={{ fill: '#ff5722', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonthlyAutomationStatusChart;
